// Feedback Review, layout B "side rail" (2026-10-26, docs/feedback.md): the
// pure parts of the page, so they run under node --test without a DOM.

import { convNumberPath } from '../../contactPath.js'
import { missingScreenshotText } from '../../feedbackScreenshotStatus.js'

// The feedback text shows 4 lines of 21px, cut with no fade; "Show more"
// expands it in place.
export const FEEDBACK_LINE_HEIGHT_PX = 21
export const FEEDBACK_COLLAPSED_LINES = 4
export const FEEDBACK_COLLAPSED_HEIGHT_PX = FEEDBACK_LINE_HEIGHT_PX * FEEDBACK_COLLAPSED_LINES

// "10/23/2026 11:58 AM ET": MM/DD/YYYY h:mm AM/PM, Eastern.
export function formatFeedbackDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d).map((x) => [x.type, x.value]))
  return `${p.month}/${p.day}/${p.year} ${p.hour}:${p.minute} ${String(p.dayPeriod ?? '').toUpperCase()} ET`
}

// "Completed 10/24/2026": the date only.
export function formatCompletedDate(iso) {
  const full = formatFeedbackDate(iso)
  return full ? full.split(' ')[0] : ''
}

// The contact opened on its Activity log tab (App.jsx reads ?view=activity).
// With the report's id (2026-10-29) the log scrolls to the report and marks
// it "Reported here".
export function activityLogPath(convNumber, feedbackId = null) {
  if (convNumber == null) return null
  return `${convNumberPath(convNumber)}?view=activity${feedbackId ? `&feedback=${encodeURIComponent(feedbackId)}` : ''}`
}

// Five lines (2026-10-27): "What the CSR hit" was dropped. An older report
// whose stored triage still has `hit` does not show it.
export const TRIAGE_ROWS = Object.freeze([
  ['did', 'What the system did'],
  ['cause', 'Likely cause'],
  ['where', 'Where to look'],
  ['similar', 'Similar reports'],
  ['next', 'Suggested next step'],
])

// The triage block's state: 'ok' with its rows, 'failed' with the reason
// (Retry offered), or 'pending' (not written yet, or the job is off).
export function triageView(triage) {
  if (triage?.status === 'ok') {
    return { state: 'ok', rows: TRIAGE_ROWS.map(([key, label]) => ({ key, label, value: triage[key] ?? '' })).filter((r) => r.value) }
  }
  if (triage?.status === 'failed') return { state: 'failed', text: `Not generated (${triage.reason ?? 'unknown'})` }
  return { state: 'pending', text: 'Not generated yet' }
}

export function similarText(count) {
  const n = Number(count)
  if (!Number.isFinite(n) || n <= 0) return null
  return `${n} similar in 14 days`
}

// The rail's width (2026-10-27, 1.5x the first layout's 280 / 320).
export const RAIL_BASIS_PX = 420
export const RAIL_MAX_WIDTH_PX = 480

// What sits in the row above FEEDBACK: the captured screenshot(s), or the
// muted text saying why there is none, or nothing. A report holds one
// screenshot today; `ids` is a list so more than one shows side by side.
export function screenshotSlot(item) {
  if (item?.hasScreenshot) return { kind: 'images', ids: [item.id] }
  if (item?.screenshotCapturedAt) return { kind: 'text', text: 'Screenshot expired', testId: 'feedback-screenshot-expired' }
  const missing = missingScreenshotText(item?.screenshotStatus)
  return missing ? { kind: 'text', text: missing, testId: 'feedback-no-screenshot' } : null
}

// The page's filters: Company, Channel, Created By, Assigned To, Intent and
// Group ('' = no filter; '__unassigned__' = no assignee).
export const UNASSIGNED_FILTER_VALUE = '__unassigned__'
export function filterFeedback(items, { company = '', channel = '', submittedBy = '', assigneeId = '', intent = '', groupKey = '' } = {}) {
  return (items ?? []).filter((i) =>
    (!company || i.company === company) &&
    (!channel || i.channel === channel) &&
    (!submittedBy || i.submittedBy === submittedBy) &&
    (!assigneeId || (assigneeId === UNASSIGNED_FILTER_VALUE ? !i.assigneeId : i.assigneeId === assigneeId)) &&
    (!intent || i.intent === intent) &&
    (!groupKey || i.groupKey === groupKey))
}

// The Group filter's options: every group the loaded reports carry, named
// where a name exists.
export function groupOptions(items, groups = []) {
  const names = new Map((groups ?? []).map((g) => [g.key, g.name]))
  const seen = new Map()
  for (const i of items ?? []) {
    if (!i.groupKey || seen.has(i.groupKey)) continue
    seen.set(i.groupKey, i.groupName ?? names.get(i.groupKey) ?? i.groupKey)
  }
  return [...seen.entries()].map(([key, name]) => ({ key, name })).sort((a, b) => a.name.localeCompare(b.name))
}

// A PATCH answer is the bare row (no contact number, order or group count);
// keep what the list query joined in.
export function mergeUpdated(prev, updated) {
  const keep = ['convNumber', 'orderNumber', 'groupName', 'groupCount14d', 'company', 'channel', 'snapshot', 'triage', 'groupKey']
  const out = { ...prev, ...updated }
  for (const k of keep) if (updated?.[k] == null && prev?.[k] != null) out[k] = prev[k]
  return out
}
